import React, {useEffect, useMemo, useState} from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import SearchRoundedIcon from "@mui/icons-material/SearchRounded"
import DateRangeRoundedIcon from "@mui/icons-material/DateRangeRounded"
import {Grid, Paper, Stack, Typography} from "@mui/material"
import {IntegrationService} from "src/services/integration-service"
import ButtonWithIcon from "src/v2/components/Button/Button"
import DatePickerSelectV2 from "src/v2/components/Date/date-picker-select"
import SelectComponent from "src/v2/components/select/select-component"
import {dispatchSnackbar} from "src/v2/components/SnackBar/snackbar-bus"
import {buildReportDataTable, decodeResponsePayload} from "../report-data"
import ReportTable from "./report-table"

const ROUTINE_OPTIONS = [
    {value: "cliente_precos_log_workflow", label: "Cliente Preços Log"},
    {value: "curvas_workflow", label: "Curvas"},
    {value: "liquidez_workflow", label: "Liquidez"},
    {value: "precos_processados_workflow_v2", label: "Preços Processados V2"},
    {value: "precos_eventos_financeiros_workflow", label: "Preços Eventos Financeiros"},
    {value: "precos_eventos_lista_workflow", label: "Preços Eventos Lista"},
    {value: "precos_tunel_workflow", label: "Preços Túneis"},
    {value: "spreads_brutos_tratados_workflow", label: "Spreads Brutos Tratados"},
    {value: "spreads_negociados_workflow", label: "Spreads Negociados"},
    {value: "spreads_emissao_workflow", label: "Spreads Emissão"},
    {value: "spread_relativo_workflow", label: "Spread Relativo"},
    {value: "tratamento_risk_free_v2_workflow", label: "Tratamento Risk Free V2"},
    {value: "tratamento_risk_free_workflow", label: "Tratamento Risk Free legado"},
    {value: "curvas_volatilidade_workflow", label: "Curvas Volatilidade"},
]

const ReportDataTab = ({onLoadingChange}) => {
    const service = useMemo(() => new IntegrationService(), [])
    const [routine, setRoutine] = useState("")
    const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"))
    const [loading, setLoading] = useState(false)
    const [table, setTable] = useState({rows: [], columns: []})

    useEffect(() => () => onLoadingChange(false), [onLoadingChange])

    const load = async (period = false) => {
        if (!routine || !date) {
            dispatchSnackbar("Selecione uma rotina e uma data", "warning")
            return
        }
        setLoading(true)
        onLoadingChange(true)
        setTable({rows: [], columns: []})
        try {
            const response = period
                ? await service.getReportDataPeriodoInfo(routine, date)
                : await service.getReportDataInfo(routine, date)
            const result = buildReportDataTable(decodeResponsePayload(response))
            setTable(result)
            if (result.rows.length === 0) dispatchSnackbar("Nenhum dado disponível", "warning")
        } catch (_) {
            dispatchSnackbar("Erro ao buscar ou processar o relatório", "error")
        } finally {
            setLoading(false)
            onLoadingChange(false)
        }
    }

    return (
        <Stack spacing={3} sx={{pt: 3}}>
            <Paper sx={{p: 3, backgroundColor: "background.paper"}}>
                <Stack spacing={3}>
                    <BoxHeader/>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <SelectComponent
                                label="Rotina"
                                value={routine}
                                onChange={(event) => setRoutine(event.target.value)}
                                options={ROUTINE_OPTIONS}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <DatePickerSelectV2
                                label="Data de exibição"
                                value={date}
                                onChange={(value) => {
                                    if (value && dayjs(value).isValid()) setDate(dayjs(value).format("YYYY-MM-DD"))
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={5}>
                            <Stack direction={{xs: "column", sm: "row"}} spacing={1.5}>
                                <ButtonWithIcon icon={<SearchRoundedIcon/>} onClick={() => load(false)} disabled={loading}>
                                    CONSULTAR
                                </ButtonWithIcon>
                                <ButtonWithIcon icon={<DateRangeRoundedIcon/>} onClick={() => load(true)} disabled={loading}>
                                    CONSULTAR 15 DIAS
                                </ButtonWithIcon>
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            </Paper>
            <ReportTable title="Relatório" rows={table.rows} columns={table.columns} loading={loading}/>
        </Stack>
    )
}

const BoxHeader = () => (
    <div>
        <Typography variant="h3" color="common.white">Relatório de dados</Typography>
        <Typography variant="body2" color="primary.white70" sx={{mt: 0.5}}>
            Consulte os resultados registrados por uma rotina e data de referência.
        </Typography>
    </div>
)

ReportDataTab.propTypes = {onLoadingChange: PropTypes.func.isRequired}

export default ReportDataTab
